import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as RecipeActions from './recipe.actions';
import { RecipeService } from '../../services/recipe.service';
import { Store } from '@ngrx/store';
import { selectRecipeFilters, selectRecipePagination, selectRecipeSearchTerm } from './recipe.selectors';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { ApprovalStatus } from '../../../shared/enums/approval-status.enum';
import { TranslateService } from '@ngx-translate/core';

export const loadRecipes$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    store = inject(Store),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadRecipes),
      withLatestFrom(
        store.select(selectRecipePagination),
        store.select(selectRecipeSearchTerm),
        store.select(selectRecipeFilters)
      ),
      exhaustMap(([_, pagination, searchTerm, filters]) =>
        recipeService.getPaginated(pagination.pageIndex, pagination.pageSize, searchTerm, filters).pipe(
          map(({ recipes, total }) => RecipeActions.loadRecipesSuccess({ recipes, total })),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.loadFailed'));
            return of(RecipeActions.loadRecipesFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadPendingRecipes$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    store = inject(Store),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadPendingRecipes),
      withLatestFrom(
        store.select(selectRecipePagination),
        store.select(selectRecipeSearchTerm),
        store.select(selectRecipeFilters)
      ),
      exhaustMap(([_, pagination, searchTerm, filters]) =>
        recipeService.getPendingPaginated(pagination.pageIndex, pagination.pageSize, searchTerm, filters).pipe(
          map(({ recipes, total }) => RecipeActions.loadPendingRecipesSuccess({ recipes, total })),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.loadPendingFailed'));
            return of(RecipeActions.loadPendingRecipesFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadRecipeDetails$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadRecipeDetails),
      exhaustMap(({ slug }) =>
        recipeService.getRecipeDetailsBySlug(slug).pipe(
          map(details => RecipeActions.loadRecipeDetailsSuccess({ details })),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.loadDetailsFailed'));
            return of(RecipeActions.loadRecipeDetailsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const handleRecipeLoadFailure$ = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router)
  ) => {
    return actions$.pipe(
      ofType(RecipeActions.loadRecipeDetailsFailure),
      tap(({ error }) => {
        if (error?.status === 404) {
          router.navigate(['/recipes/not-found']);
        }
      })
    );
  },
  { functional: true, dispatch: false }
);


export const createRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.createRecipe),
      exhaustMap(({ recipe, ingredients, possibleLeftovers, leftoverUsages }) =>
        recipeService.create(recipe, ingredients, possibleLeftovers, leftoverUsages).pipe(
          map(created => {
            notification.success(translate.instant('recipe.notifications.createSuccess'));
            return RecipeActions.createRecipeSuccess({ recipe: created });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.createFailed'));
            return of(RecipeActions.createRecipeFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.updateRecipe),
      exhaustMap(({ id, recipe, ingredients, possibleLeftovers, leftoverUsages }) =>
        recipeService.update(id, recipe, ingredients, possibleLeftovers, leftoverUsages).pipe(
          map(updated => {
            notification.success(translate.instant('recipe.notifications.updateSuccess'));
            return RecipeActions.updateRecipeSuccess({ recipe: updated });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.updateFailed'));
            return of(RecipeActions.updateRecipeFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.deleteRecipe),
      exhaustMap(({ id }) =>
        recipeService.delete(id).pipe(
          map(() => {
            notification.success(translate.instant('recipe.notifications.deleteSuccess'));
            return RecipeActions.deleteRecipeSuccess({ id });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.deleteFailed'));
            return of(RecipeActions.deleteRecipeFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadUserRecipes$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    store = inject(Store),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadUserRecipes),
      withLatestFrom(
        store.select(selectRecipePagination),
        store.select(selectRecipeSearchTerm),
        store.select(selectRecipeFilters)
      ),
      exhaustMap(([_, pagination, searchTerm, filters]) =>
        recipeService.getUserRecipes(pagination.pageIndex, pagination.pageSize, searchTerm, filters).pipe(
          map(({ recipes, total }) => RecipeActions.loadUserRecipesSuccess({ recipes, total })),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.loadUserFailed'));
            return of(RecipeActions.loadUserRecipesFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

// Load Saved Recipes Effect
export const loadSavedRecipes$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    store = inject(Store),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadSavedRecipes),
      withLatestFrom(
        store.select(selectRecipePagination),
        store.select(selectRecipeSearchTerm),
        store.select(selectRecipeFilters)
      ),
      exhaustMap(([_, pagination, searchTerm, filters]) =>
        recipeService.getSavedRecipes(pagination.pageIndex, pagination.pageSize, searchTerm, filters).pipe(
          map(({ recipes, total }) => RecipeActions.loadSavedRecipesSuccess({ recipes, total })),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.loadSavedFailed'));
            return of(RecipeActions.loadSavedRecipesFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

// Load Other User's Public Recipes Effect
export const loadOtherUserRecipes$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    store = inject(Store),
    router = inject(Router),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadOtherUserRecipes),
      withLatestFrom(
        store.select(selectRecipePagination),
        store.select(selectRecipeSearchTerm),
        store.select(selectRecipeFilters)
      ),
      exhaustMap(([{ username }, pagination, searchTerm, filters]) =>
        recipeService
          .getOtherUserRecipes(username, pagination.pageIndex, pagination.pageSize, searchTerm, filters)
          .pipe(
            map(({ recipes, total, author_name }) =>
              RecipeActions.loadOtherUserRecipesSuccess({ recipes, total, author_name })
            ),
            catchError(error => {
              if (error?.status === 404) {
                router.navigate(['/users', 'not-found']);
              } else {
                notification.error(translate.instant('recipe.notifications.loadOtherFailed'));
              }
              return of(RecipeActions.loadOtherUserRecipesFailure({ error }));
            })
          )
      )
    ),
  { functional: true }
);



export const saveRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.saveRecipe),
      mergeMap(({ recipeId }) =>
        recipeService.saveRecipe(recipeId).pipe(
          map(() => {
            notification.success(translate.instant('recipe.notifications.saveSuccess'));
            return RecipeActions.saveRecipeSuccess({ recipeId });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.saveFailed'));
            return of(RecipeActions.saveRecipeFailure({ recipeId, error }));
          })
        )
      )
    ),
  { functional: true }
);

export const unsaveRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.unsaveRecipe),
      mergeMap(({ recipeId }) =>
        recipeService.unsaveRecipe(recipeId).pipe(
          map(() => {
            notification.success(translate.instant('recipe.notifications.unsaveSuccess'));
            return RecipeActions.unsaveRecipeSuccess({ recipeId });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.unsaveFailed'));
            return of(RecipeActions.unsaveRecipeFailure({ recipeId, error }));
          })
        )
      )
    ),
  { functional: true }
);

export const rateRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.rateRecipe),
      exhaustMap(({ recipeId, slug, rating }) =>
        recipeService.rateRecipe(recipeId, rating).pipe(
          map(() => {
            notification.success(translate.instant('recipe.notifications.rateSuccess'));
            return RecipeActions.rateRecipeSuccess({ recipeId, slug, rating });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.rateFailed'));
            return of(RecipeActions.rateRecipeFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);


export const rateRecipeSuccessReload$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(RecipeActions.rateRecipeSuccess),
      tap(({ slug }) => {
        store.dispatch(RecipeActions.loadRecipeDetails({ slug }));
      })
    ),
  { functional: true, dispatch: false }
);

export const unrateRecipe$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.unrateRecipe),
      exhaustMap(({ recipeId, slug }) =>
        recipeService.unrateRecipe(recipeId).pipe(
          map(() => {
            notification.success(translate.instant('recipe.notifications.unrateSuccess'));
            return RecipeActions.unrateRecipeSuccess({ recipeId, slug });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.unrateFailed'));
            return of(RecipeActions.unrateRecipeFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const unrateRecipeSuccessReload$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(RecipeActions.unrateRecipeSuccess),
      tap(({ slug }) => {
        store.dispatch(RecipeActions.loadRecipeDetails({ slug }));
      })
    ),
  { functional: true, dispatch: false }
);

export const updateApproval$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.updateRecipeApproval),
      mergeMap(({ id, status, reason }) =>
        recipeService.updateApproval(id, status, reason).pipe(
          map(recipe => {
            if (status === ApprovalStatus.Approved) {
              notification.success(translate.instant('recipe.notifications.approvalApproved'));
            } else {
              notification.success(translate.instant('recipe.notifications.approvalDenied'));
            }
            return RecipeActions.updateRecipeApprovalSuccess({ recipe });
          }),
          catchError(error => {
            notification.error(translate.instant('recipe.notifications.approvalFailed'));
            return of(RecipeActions.updateRecipeApprovalFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const navigateAfterApprovalSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(RecipeActions.updateRecipeApprovalSuccess),
      tap(() => router.navigate(['/pending']))
    ),
  { functional: true, dispatch: false }
);

export const loadLikeQuota$ = createEffect(
  (
    actions$ = inject(Actions),
    recipeService = inject(RecipeService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(RecipeActions.loadLikeQuota),
      switchMap(() =>
        recipeService.getLikeQuota().pipe(
          map((quota) => {
            // Optional success message
            // notification.success('Like quota loaded.');
            return RecipeActions.loadLikeQuotaSuccess({ quota });
          }),
          catchError(() => {
            notification.error(translate.instant('recipe.notifications.quotaFailure'));
            return of(RecipeActions.loadLikeQuotaFailure());
          })
        )
      )
    ),
  { functional: true }
);

export const refreshLikeQuotaOnAdd$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(RecipeActions.saveRecipeSuccess),
      map(() => RecipeActions.loadLikeQuota())
    ),
  { functional: true }
);

export const refreshLikeQuotaOnComplete$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(RecipeActions.unsaveRecipeSuccess),
      map(() => RecipeActions.loadLikeQuota())
    ),
  { functional: true }
);


export const RecipeEffects = {
    loadRecipes$,
    loadRecipeDetails$,
    createRecipe$,
    updateRecipe$,
    deleteRecipe$,
    loadUserRecipes$,
    saveRecipe$,
    unsaveRecipe$,
    loadSavedRecipes$,
    loadOtherUserRecipes$,
    rateRecipe$,
    rateRecipeSuccessReload$,
    loadPendingRecipes$,
    updateApproval$,
    navigateAfterApprovalSuccess$,
    handleRecipeLoadFailure$,
    unrateRecipe$,
    unrateRecipeSuccessReload$,
    loadLikeQuota$,
    refreshLikeQuotaOnAdd$,
    refreshLikeQuotaOnComplete$
  };
  
